<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Coupon</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a new discount coupon for customers
                      </p>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-ticket</v-icon>
                      New Coupon Form
                    </v-chip>
                    <v-btn color="grey darken-1" outlined @click="$router.back()" class="back-button">
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Coupons
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Coupon Details Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-ticket-percent</v-icon>
                    Coupon Details
                  </h3>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.code"
                        label="Coupon Code *" placeholder="e.g. SAVE20" outlined
                        :rules="[(v) => !!v || 'Coupon code is required']"
                        :error-messages="errors.code"
                        class="professional-field" hide-details="auto" required
                        @input="form.code = form.code ? form.code.toUpperCase() : ''"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-ticket-percent</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.discount_type_id"
                        :items="discountTypeItems"
                        item-text="label"
                        item-value="id"
                        label="Discount Type *" outlined
                        :rules="[(v) => !!v || 'Discount type is required']"
                        :error-messages="errors.discount_type_id"
                        class="professional-field" hide-details="auto" required
                        :loading="isLoadingDropdown"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">{{ isPercentage ? 'mdi-percent' : 'mdi-currency-inr' }}</v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-icon small class="mr-2">{{ item.code === 'PERCENTAGE' ? 'mdi-percent' : 'mdi-currency-inr' }}</v-icon>
                          {{ item.label }}
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.discount_value"
                        :label="isPercentage ? 'Discount Percentage *' : 'Discount Amount (₹) *'"
                        :placeholder="isPercentage ? 'e.g. 20' : 'e.g. 100'"
                        outlined type="number"
                        :rules="[
                          (v) => !!v || 'Discount value is required',
                          (v) => v > 0 || 'Must be greater than 0',
                          (v) => !isPercentage || v <= 100 || 'Percentage cannot exceed 100',
                        ]"
                        :error-messages="errors.discount_value"
                        class="professional-field" hide-details="auto" required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">{{ form.discount_type === 'percentage' ? 'mdi-percent' : 'mdi-currency-inr' }}</v-icon>
                        </template>
                        <template v-slot:append v-if="isPercentage">
                          <span class="grey--text">%</span>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.description"
                        label="Description (Optional)" placeholder="Enter coupon description"
                        outlined rows="3"
                        :error-messages="errors.description"
                        class="professional-field" hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-text</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>

                <!-- Limits Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-tune</v-icon>
                    Limits & Restrictions
                  </h3>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.min_order_amount"
                        label="Minimum Order Amount (₹)" placeholder="e.g. 500"
                        outlined type="number"
                        :rules="[(v) => !v || v >= 0 || 'Must be 0 or greater']"
                        :error-messages="errors.min_order_amount"
                        class="professional-field" hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-currency-inr</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4" v-if="isPercentage">
                      <v-text-field
                        v-model="form.max_discount_amount"
                        label="Max Discount Amount (₹)" placeholder="e.g. 200"
                        outlined type="number"
                        :rules="[(v) => !v || v >= 0 || 'Must be 0 or greater']"
                        :error-messages="errors.max_discount_amount"
                        class="professional-field" hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-currency-inr</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                  </v-row>
                </div>

                <!-- Usage Limits & Status Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-tune</v-icon>
                    Usage Limits & Status
                  </h3>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.usage_limit"
                        label="Total Usage Limit"
                        placeholder="e.g. 100 (blank = unlimited)"
                        outlined type="number" min="0"
                        :error-messages="errors.usage_limit"
                        class="professional-field" hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.usage_limit_per_user"
                        label="Usage Limit Per User"
                        placeholder="e.g. 1 (blank = unlimited)"
                        outlined type="number" min="0"
                        :error-messages="errors.usage_limit_per_user"
                        class="professional-field" hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-card outlined class="status-card pa-2">
                        <div class="d-flex align-center">
                          <v-icon color="grey" class="mr-2">mdi-toggle-switch</v-icon>
                          <div class="flex-grow-1">
                            <div class="font-weight-medium">Status</div>
                            <v-chip x-small :color="form.is_active ? 'success' : 'error'" dark class="mt-1">
                              {{ form.is_active ? 'Active' : 'Inactive' }}
                            </v-chip>
                          </div>
                          <v-switch v-model="form.is_active" color="success" inset hide-details class="ml-2"></v-switch>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>

                <!-- Validity Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-calendar-range</v-icon>
                    Validity Period
                  </h3>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-menu v-model="validFromMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.valid_from" label="Valid From"
                            prepend-inner-icon="mdi-calendar" outlined readonly clearable
                            :error-messages="errors.valid_from"
                            class="professional-field" hide-details="auto"
                            v-bind="attrs" v-on="on"
                            @click:clear="form.valid_from = null"
                          ></v-text-field>
                        </template>
                        <v-date-picker v-model="form.valid_from" @input="validFromMenu = false"></v-date-picker>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-menu v-model="validUntilMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="form.valid_until" label="Valid Until"
                            prepend-inner-icon="mdi-calendar" outlined readonly clearable
                            :error-messages="errors.valid_until"
                            class="professional-field" hide-details="auto"
                            v-bind="attrs" v-on="on"
                            @click:clear="form.valid_until = null"
                          ></v-text-field>
                        </template>
                        <v-date-picker v-model="form.valid_until" :min="form.valid_from" @input="validUntilMenu = false"></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </div>

                <!-- User Assignment Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
                    User Assignment
                  </h3>

                  <v-row class="mb-2">
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="form.position_id"
                        :items="positionItems"
                        item-text="name"
                        item-value="id"
                        label="Applicable For"
                        prepend-inner-icon="mdi-account-badge"
                        outlined
                        clearable
                        hide-details="auto"
                        class="professional-field"
                        :loading="isLoadingDropdown"
                        placeholder="All Users"
                      ></v-select>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3">mdi-earth</v-icon>
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">Availability</div>
                              <div class="text-caption grey--text">
                                For everyone or restrict to specific users
                              </div>
                            </div>
                            <v-switch
                              v-model="form.is_global"
                              color="success" inset hide-details class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <status-chip :color="form.is_global ? 'success' : 'primary'" :icon="form.is_global ? 'mdi-earth' : 'mdi-account-multiple'" :label="String(form.is_global ? 'For Everyone' : 'Specific Users')" />
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <template v-if="!form.is_global">
                    <v-row class="mt-3">
                      <v-col cols="12" md="8">
                        <v-autocomplete
                          v-model="userToAdd"
                          :items="filteredAvailableUsers"
                          item-text="display_name"
                          item-value="id"
                          label="Search by phone number"
                          outlined dense clearable hide-details
                          prepend-inner-icon="mdi-phone"
                          :loading="isLoadingUsers"
                          no-data-text="No users found"
                          @change="onUserSelected"
                          return-object
                        >
                          <template v-slot:item="{ item }">
                            <v-list-item-content>
                              <v-list-item-title>
                                <v-icon x-small class="mr-1" color="grey">mdi-phone</v-icon>
                                {{ item.phone || 'No phone' }}
                              </v-list-item-title>
                              <v-list-item-subtitle class="text-caption">
                                {{ item.user_name || item.first_name }}
                                <span v-if="item.email" class="ml-1 grey--text">· {{ item.email }}</span>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>

                    <v-row v-if="selectedUsers.length > 0" class="mt-2">
                      <v-col cols="12">
                        <p class="text-caption grey--text mb-1">Selected users ({{ selectedUsers.length }}):</p>
                        <v-chip
                          v-for="user in selectedUsers"
                          :key="user.id"
                          close small class="mr-2 mb-2"
                          color="primary" outlined
                          @click:close="removeSelectedUser(user.id)"
                        >
                          <v-icon left x-small>mdi-account</v-icon>
                          {{ user.user_name }}
                        </v-chip>
                      </v-col>
                    </v-row>

                    <v-alert v-else type="info" text class="mt-3" dense>
                      Search and select users above. They will be assigned after the coupon is saved.
                    </v-alert>
                  </template>
                </div>

              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      text-color="white" class="mr-3"
                    >
                      <v-icon left x-small>
                        {{ hasValidationErrors() ? "mdi-alert-circle" : "mdi-check-circle" }}
                      </v-icon>
                      {{ hasValidationErrors() ? "Please fix validation errors" : "Form validation passed" }}
                    </v-chip>
                    <span class="text-caption grey--text">All required fields must be completed</span>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn color="grey" outlined class="mr-3 action-button" @click="$router.back()" :disabled="isSaving">
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary" class="action-button"
                      @click="saveCoupon"
                      :loading="isSaving"
                      :disabled="isSaving || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Coupon
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon large style="color: green; font-size: 90px; width: 200px !important">mdi-check-circle</v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Coupon created successfully.</h3>
                      <br />
                      <v-btn color="success" @click="showSuccessDialog = false; $router.push('/coupons')">OK</v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "CouponCreatePage",

  data() {
    return {
      valid: false,
      form: {
        code: "",
        discount_type_id: null,
        discount_value: "",
        description: "",
        min_order_amount: "",
        max_discount_amount: "",
        usage_limit: "",
        usage_limit_per_user: "",
        valid_from: null,
        valid_until: null,
        is_active: true,
        position_id: null,
        is_global: true,
      },
      errors: {},
      isSaving: false,
      showSuccessDialog: false,
      validFromMenu: false,
      validUntilMenu: false,
      discountTypes: [],
      positions: [],
      isLoadingDropdown: false,
      // User assignment
      availableUsers: [],
      selectedUsers: [],
      userToAdd: null,
      isLoadingUsers: false,
    };
  },

  computed: {
    positionItems() {
      return [{ id: null, name: 'All Users' }, ...this.positions.filter(p => p.name !== 'SELLER')];
    },
    filteredAvailableUsers() {
      const selectedIds = this.selectedUsers.map((u) => u.id);
      const selectedPosition = this.positions.find(p => p.id === this.form.position_id);
      return this.availableUsers.filter((u) => {
        if (selectedIds.includes(u.id)) return false;
        // Filter by position if one is selected
        if (selectedPosition) {
          return (u.position_name || '').toUpperCase() === selectedPosition.name.toUpperCase();
        }
        return true;
      });
    },
    discountTypeItems() {
      return this.discountTypes.map((dt) => ({
        ...dt,
        label: dt.code === "PERCENTAGE" ? `${dt.description} (%)` : `${dt.description} (₹)`,
      }));
    },
    selectedDiscountType() {
      return this.discountTypes.find((d) => d.id === this.form.discount_type_id) || null;
    },
    isPercentage() {
      const dt = this.selectedDiscountType;
      if (!dt) return false;
      const text = (dt.code || dt.description || "").toLowerCase();
      return text.includes("percent");
    },
  },

  mounted() {
    this.fetchCouponDropdown();
    this.fetchAvailableUsers();
  },

  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async fetchCouponDropdown() {
      this.isLoadingDropdown = true;
      try {
        const res = await axios.get("/coupons/dropdown");
        if (res.data.success) {
          this.discountTypes = res.data.data.discount_types || [];
          this.positions = res.data.data.positions || [];
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingDropdown = false;
      }
    },

    async fetchAvailableUsers() {
      this.isLoadingUsers = true;
      try {
        const res = await axios.get("/user_coupons/dropdown");
        if (res.data.success) {
          this.availableUsers = (res.data.data.users || []).map((u) => ({
            ...u,
            display_name: u.phone ? String(u.phone) : (u.user_name || ''),
          }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingUsers = false;
      }
    },

    onUserSelected(user) {
      if (user && !this.selectedUsers.find((u) => u.id === user.id)) {
        this.selectedUsers.push(user);
      }
      this.$nextTick(() => {
        this.userToAdd = null;
      });
    },

    removeSelectedUser(userId) {
      this.selectedUsers = this.selectedUsers.filter((u) => u.id !== userId);
    },

    async saveCoupon() {
      if (!this.$refs.form.validate()) return;

      this.isSaving = true;
      this.errors = {};

      try {
        const couponData = {
          code: this.form.code,
          discount_type_id: this.form.discount_type_id,
          discount_value: this.form.discount_value,
          description: this.form.description || null,
          min_order_amount: this.form.min_order_amount || null,
          max_discount_amount: this.form.max_discount_amount || null,
          usage_limit: this.form.usage_limit || null,
          usage_limit_per_user: this.form.usage_limit_per_user || null,
          valid_from: this.form.valid_from || null,
          valid_until: this.form.valid_until || null,
          is_active: this.form.is_active,
          position_id: this.form.position_id || null,
          is_global: this.form.is_global,
        };

        const response = await axios.post("/coupons", couponData);

        if (response.data.success) {
          const createdCoupon = response.data.data;

          // Assign selected users if any
          if (!this.form.is_global && this.selectedUsers.length > 0) {
            await Promise.allSettled(
              this.selectedUsers.map((user) =>
                axios.post("/user_coupons", {
                  coupon_id: createdCoupon.id,
                  user_id: user.id,
                })
              )
            );
          }

          this.resetForm();
          this.showSuccessDialog = true;
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "An error occurred while creating the coupon.",
          });
        }
      } catch (error) {
        console.error(error);
        if (error.response) {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$store.dispatch("errors/setError", { message: "Authentication required. Please login again." });
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message: error.response.data.message || "An error occurred while creating the coupon.",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    resetForm() {
      this.form = {
        code: "",
        discount_type_id: null,
        discount_value: "",
        description: "",
        min_order_amount: "",
        max_discount_amount: "",
        usage_limit: "",
        usage_limit_per_user: "",
        valid_from: null,
        valid_until: null,
        is_active: true,
        position_id: null,
        is_global: true,
      };
      this.selectedUsers = [];
      this.userToAdd = null;
      this.errors = {};
      this.$refs.form.resetValidation();
    },
  },

  watch: {
    "form.code"() {
      if (this.errors.code) this.$delete(this.errors, "code");
    },
    "form.discount_type_id"() {
      if (this.errors.discount_type_id) this.$delete(this.errors, "discount_type_id");
      this.form.max_discount_amount = "";
    },
    "form.discount_value"() {
      if (this.errors.discount_value) this.$delete(this.errors, "discount_value");
    },
    "form.position_id"() {
      // Clear selected users when position changes — they may not match new position
      this.selectedUsers = [];
      this.userToAdd = null;
    },
  },
};
</script>

<style scoped>
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
